import React from 'react';
import { User } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'motion/react';

interface SessionSentPromptProps {
  user: User;
  onDismiss: () => void;
}

// Shown right after a session goes out. Cross-promotes the separate "Yhuu
// Test" compatibility quiz — same personal test link used on the Profile
// page (see quizSupabase.ts / Profile.tsx testLink). "Take the Yhuu Test"
// is a full page nav since that quiz is a different app served via a
// Vercel rewrite; dismissing just drops them on their dashboard.
export default function SessionSentPrompt({ user, onDismiss }: SessionSentPromptProps) {
  const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
  const testLink = `/test?yhuu_uid=${encodeURIComponent(user.id)}&yhuu_name=${encodeURIComponent(displayName)}`;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="absolute inset-0"
          style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
        />

        <motion.div
          className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl text-center"
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
        >
          {/* Gradient banner, same as the "Send Anonymously Now!" card */}
          <div
            className="px-8 py-7"
            style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)' }}
          >
            <p className="text-white font-black text-xl sm:text-2xl tracking-tight">
              Sent ✅
            </p>
          </div>

          <div className="bg-white px-8 pt-12 pb-6">
            <h2 className="text-3xl sm:text-4xl font-black leading-[1.2] tracking-tight text-zinc-900">
              Someone think they really know{' '}
              <em style={{ color: '#e11d48', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>you</em>?
            </h2>
            <p className="text-base text-zinc-500 mt-4 leading-relaxed">
              Test them. See if the people around you actually know who you are.
            </p>
          </div>

          <div className="bg-white px-8 pb-8">
            <a
              href={testLink}
              data-track="sent_prompt_test"
              className="w-full font-black text-base py-5 rounded-full flex items-center justify-center gap-2 text-white transition-transform active:scale-[0.97] cursor-pointer"
              style={{ background: 'linear-gradient(90deg, #e11d48, #ec4899)', textDecoration: 'none' }}
            >
              Take The Yhuu Test
            </a>

            <button
              onClick={onDismiss}
              data-track="sent_prompt_dismiss"
              className="text-zinc-500 text-sm font-semibold text-center hover:text-zinc-700 transition-colors mt-5 w-full cursor-pointer"
              style={{ background: 'none', border: 'none' }}
            >
              No one really gets me 🥲
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
